import React, { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import { Trans } from "react-i18next";
import { InfoOutlined } from "@mui/icons-material";
import { TestResultsPartialBanner } from "./TestResults.styles";
import { getUserAuth } from "../../recoil/selectors";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";

type ProcessingTest = "carrier" | "aneuploidy";

interface PartialResultsBannerProps {
  /** Status of the test that is still processing. */
  processingStatus: string | undefined;
  /**
   * Truthy when the OTHER test in the pair has a final/valid result.
   * The banner is the visual cue that one test is done while the other
   * is still in progress, so we only render when both halves of that
   * condition hold.
   */
  otherStatusValid: string | boolean | undefined;
  estimatedDate: string | null | undefined;
  /** Display name shown in the banner copy (e.g. "Carrier Screen"). */
  testName: string;
  /** Used as the `processing_test` analytics property. */
  processingTest: ProcessingTest;
  requisitionID: string;
}

/**
 * Visual partial-results banner plus its impression tracking.
 *
 * Rendering and tracking are deliberately co-located so the analytics
 * event cannot drift from the render condition: if this component is
 * mounted, it will attempt to fire exactly one `impression` event the
 * first time the banner element actually intersects the viewport.
 */
export const PartialResultsBanner: React.FC<PartialResultsBannerProps> = ({
  processingStatus,
  otherStatusValid,
  estimatedDate,
  testName,
  processingTest,
  requisitionID,
}) => {
  const auth = useRecoilValue(getUserAuth);
  const bannerRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  const shouldRender =
    processingStatus === "Processing" && Boolean(otherStatusValid);

  useEffect(() => {
    if (!shouldRender) return;
    const el = bannerRef.current;
    if (!el || hasFiredRef.current) return;
    if (!auth?.access_token) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasFiredRef.current) {
            hasFiredRef.current = true;
            TrackAnalyticsEvent(
              AnalyticsEventNames.impression,
              auth.access_token,
              auth.user_id,
              {
                element_name: AnalyticsEventNames.PartialResultsBannerShown,
                processing_test: processingTest,
                req_identifier: requisitionID,
              },
            );
            observer.disconnect();
            return;
          }
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    shouldRender,
    auth?.access_token,
    auth?.user_id,
    processingTest,
    requisitionID,
  ]);

  if (!shouldRender) return null;

  const formattedDate = estimatedDate
    ? new Date(`${estimatedDate}T12:00:00`).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <TestResultsPartialBanner ref={bannerRef}>
      <InfoOutlined style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
      <span>
        {formattedDate ? (
          <Trans
            i18nKey="results.dashboard.partialProcessingWithDate"
            values={{ testName, date: formattedDate }}
            components={{ strong: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="results.dashboard.partialProcessing"
            values={{ testName }}
          />
        )}
      </span>
    </TestResultsPartialBanner>
  );
};
